import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Screen, useMonth } from '../ReportsApp'
import { useReport, type Row } from '../api'
import { addMonths, monthName, tender, n, dayDow } from '../format'
import { MonthPicker } from '../ui'
import { MonthLine } from '../../charts/MonthLine'
import { reduced } from '../../lib/motion'

/**
 * Reports Home, built to work through the cost switchover (Tim, 29 Sep: costs arrive slowly through intake, so Home must
 * be honest the whole way). Complete numbers first (net sales, what Starchive keeps, money in, owed out); profit and
 * margin only on tracked sales, with one bar splitting the month into tracked and not tracked yet; Needs attention lists
 * real problems only (older stock without a cost is never one); a ring around the star shows the switchover.
 * Tracked: consigned pieces (the fee is the profit) and purchased pieces with a cost (Shane's, Split and Masih's; Masih's
 * act like the rest of the purchased pieces: price minus cost). Always moving (Tim's picks, 29 Sep): a sales ticker in
 * the title bar, totals roll and flash green when a sale lands, the line's end breathes, Money in re-sorts, Owed out and
 * Needs attention revolve one row at a time, the ring ticks forward. Reads refresh every 30 s (sales) and 60 s (the rest).
 */

interface LogR { sales: { rows: Row[]; pre_tax: number; profit: number } }
interface OverviewR { book: { tender: string; received: number }[]; shopify: { total: number; not_in_ledger: number; differs: number; ledger?: number; mirror?: number }; ties: { shopify: { ledger: number; mirror: number; not_in_ledger: number; differs: number }; cash: { ledger: number; closes: number; close_count: number } } }
interface PayoutsR { people: Row[]; bought: Row[]; bought_total: number }
interface HealthR { issues: Row[]; days_not_closed: string[] }
interface FloorR { tiles: { pieces: number; real_cost: number; typed: number; consignors: number } }

const usd = (v: number) => '$' + Math.round(v).toLocaleString('en-US')
const pct = (v: number) => Math.round(v) + '%'
const daysIn = (m: string) => { const [y, mm] = m.split('-').map(Number); return new Date(Date.UTC(y, mm, 0)).getUTCDate() }
const ease = (k: number) => 1 - Math.pow(1 - k, 3)
const EASE = 'cubic-bezier(.22,.8,.24,1)'
const OWED_EVERY = 4200, ATT_EVERY = 5200 // the revolving lists: one row every ~4 and ~5 seconds (Tim: a slightly longer loop)

/** A number that counts from 0 the first time, then rolls to each new value; a rise after the first value flashes green. */
function Roll({ value, format = usd, ms = 800, flash = true }: { value: number | null; format?: (v: number) => string; ms?: number; flash?: boolean }) {
  const [shown, setShown] = useState(0)
  const [bump, setBump] = useState(0)
  const from = useRef(0), seen = useRef<number | null>(null)
  useEffect(() => {
    if (value == null) return
    if (flash && seen.current != null && value > seen.current + 0.004) setBump((b) => b + 1)
    seen.current = value
    if (reduced()) { setShown(value); from.current = value; return }
    const start = from.current, t0 = performance.now()
    let raf = 0
    const tick = (now: number) => { const k = Math.min(1, (now - t0) / ms); setShown(start + (value - start) * ease(k)); if (k < 1) raf = requestAnimationFrame(tick); else from.current = value }
    raf = requestAnimationFrame(tick)
    return () => { cancelAnimationFrame(raf); from.current = value }
  }, [value, ms, flash])
  return <span key={bump} className={bump ? 'rp-bump' : undefined}>{value == null ? '…' : format(shown)}</span>
}

/** A fixed window that moves up one row at a time and loops; pauses under the pointer. Under Reduce Motion it scrolls by hand. */
function Revolve({ rows, rowH, visible, every }: { rows: { key: string; node: ReactNode }[]; rowH: number; visible: number; every: number }) {
  const [i, setI] = useState(0)
  const [anim, setAnim] = useState(true)
  const hover = useRef(false)
  const count = rows.length, still = reduced(), moving = count > visible && !still
  useEffect(() => { setAnim(false); setI(0) }, [count])
  useEffect(() => {
    if (!moving) return
    const t = setInterval(() => { if (hover.current || document.hidden) return; setAnim(true); setI((x) => x + 1) }, every)
    return () => clearInterval(t)
  }, [moving, every])
  useEffect(() => {
    if (!moving || i < count) return
    const t = setTimeout(() => { setAnim(false); setI(0) }, 740)
    return () => clearTimeout(t)
  }, [i, count, moving])
  const h = count > visible ? visible * rowH : count * rowH
  return (
    <div className={'rp-rev' + (moving ? ' rp-rev--moving' : '') + (still && count > visible ? ' rp-rev--scroll' : '')} style={{ height: h }}
      onPointerEnter={() => { hover.current = true }} onPointerLeave={() => { hover.current = false }}>
      <div className="rp-rev__list" style={{ transform: `translateY(${-rowH * i}px)`, transition: anim ? `transform 760ms ${EASE}` : 'none' }}>
        {rows.map((r) => <div key={r.key} style={{ height: rowH }}>{r.node}</div>)}
        {moving && rows.map((r) => <div key={'again-' + r.key} style={{ height: rowH }} aria-hidden="true">{r.node}</div>)}
      </div>
    </div>
  )
}

/** Cumulative net sales by day of month from the sales log rows (paid, not voided, net of refunds). */
function cumulative(rows: Row[] | undefined, month: string, upTo: number): number[] {
  const byDay = new Array(upTo).fill(0)
  for (const r of rows ?? []) {
    if (r.status !== 'paid' || r.voided) continue
    const d = String(r.sold_on ?? '')
    if (!d.startsWith(month)) continue
    const i = Number(d.slice(8, 10)) - 1
    if (i >= 0 && i < upTo) byDay[i] += n(r.price_paid) - n(r.refunded)
  }
  let run = 0
  return byDay.map((v) => (run += v))
}

/** Split the month's paid lines into tracked (profit known) and not tracked yet, with what Starchive keeps. */
function split(rows: Row[]) {
  const t = { net: 0, keep: 0, toConsignors: 0, toMasih: 0, tracked: 0, profit: 0, buyNet: 0, buyProfit: 0, conNet: 0, conProfit: 0, older: 0, masihNoCost: 0, noOwner: 0, noOwnerCount: 0 }
  for (const r of rows) {
    const net = n(r.price_paid) - n(r.refunded), owner = String(r.owner ?? ''), hasCost = r.cost_at_sale != null
    t.net += net
    if (owner === 'consignor') t.toConsignors += n(r.payee_share); else if (owner === 'masih') t.toMasih += n(r.payee_share)
    if (owner === 'consignor') { t.conNet += net; t.conProfit += n(r.our_profit) }
    else if (['shane', 'split', 'masih'].includes(owner) && hasCost) { t.buyNet += net; t.buyProfit += owner === 'masih' ? net - n(r.cost_at_sale) : n(r.our_profit) }
    else if (!owner) { t.noOwner += net; t.noOwnerCount++ }
    else if (owner === 'masih') t.masihNoCost += net
    else t.older += net
  }
  t.tracked = t.buyNet + t.conNet; t.profit = t.buyProfit + t.conProfit; t.keep = t.net - t.toConsignors - t.toMasih
  return t
}

type Tone = 'red' | 'amber'
interface Item { key: string; tone: Tone; text: string; go: string; to: string }

export function Home() {
  const { month, setMonth, today, goLive } = useMonth()
  const prev = addMonths(month, -1)
  const isCurrent = month === today.slice(0, 7)
  const dim = daysIn(month), elapsed = isCurrent ? Number(today.slice(8, 10)) : dim
  const L = useReport<LogR>('log', { from: month + '-01', to: isCurrent ? today : `${month}-${String(dim).padStart(2, '0')}` }, [month])
  const P = useReport<LogR>('log', { from: prev + '-01', to: `${prev}-${String(daysIn(prev)).padStart(2, '0')}` }, [prev])
  const O = useReport<OverviewR>('overview', { month }, [month])
  const Y = useReport<PayoutsR>('payouts', { month }, [month])
  const Hh = useReport<HealthR>('health')
  const F = useReport<FloorR>('inv_floor')

  // live: the current month's sales every 30 s, the rest every 60 s (the screen is left open on a Mac)
  const refresh = useRef({ L, O, Y, Hh }); refresh.current = { L, O, Y, Hh }
  useEffect(() => {
    if (!isCurrent) return
    const a = setInterval(() => { if (!document.hidden) refresh.current.L.refresh() }, 30_000)
    const b = setInterval(() => { if (!document.hidden) { refresh.current.O.refresh(); refresh.current.Y.refresh(); refresh.current.Hh.refresh() } }, 60_000)
    return () => { clearInterval(a); clearInterval(b) }
  }, [isCurrent])

  // 1. the month
  const paid = L.data ? L.data.sales.rows.filter((r) => r.status === 'paid' && !r.voided) : []
  const S = L.data ? split(paid) : null
  const cur = L.data ? cumulative(L.data.sales.rows, month, elapsed) : null
  const last = P.data ? cumulative(P.data.sales.rows, prev, daysIn(prev)) : null
  const net = L.data ? n(L.data.sales.pre_tax) : null
  const count = L.data ? new Set(paid.map((r) => String(r.sale_no))).size : null
  const avg = net != null && count ? net / count : null
  const samePoint = last && cur && cur.length ? last[Math.min(cur.length, last.length) - 1] ?? 0 : null
  const change = net != null && samePoint ? ((cur![cur!.length - 1] - samePoint) / samePoint) * 100 : null
  const share = S && S.net > 0 ? (100 * S.tracked) / S.net : null
  const complete = share != null && share >= 95
  const margin = S && S.tracked > 0 ? (100 * S.profit) / S.tracked : null

  // the ticker: this month's latest sales, newest first; sales that arrive while the screen is open come in green
  const sales = new Map<string, { no: string; v: number; how: string; at: string }>()
  for (const r of paid) {
    const k = String(r.sale_no ?? r.shopify_order_name ?? r.line_id)
    const s = sales.get(k) ?? { no: r.sale_no != null ? '#' + r.sale_no : String(r.shopify_order_name ?? ''), v: 0, how: r.channel === 'web' ? 'Website' : r.payment_method === 'split' ? 'Split pay' : tender(r.payment_method), at: String(r.sold_at ?? '') }
    s.v += n(r.price_paid) - n(r.refunded); sales.set(k, s)
  }
  const recent = [...sales.entries()].filter(([, s]) => s.v > 0).sort((a, b) => (a[1].at < b[1].at ? 1 : -1)).slice(0, 10)
  const seen = useRef<Set<string> | null>(null)
  if (L.data && seen.current == null) seen.current = new Set(recent.map(([k]) => k))

  // 2. money in (received, tax included: the counter's tenders plus the card orders in Shopify) and owed out
  const inRows = O.data ? [...O.data.book.map((b) => ({ name: tender(b.tender), v: n(b.received) })), { name: 'Card', v: n(O.data.shopify.total) }]
    .reduce((acc, r) => { const f = acc.find((a) => a.name === r.name); if (f) f.v += r.v; else acc.push({ ...r }); return acc }, [] as { name: string; v: number }[])
    .filter((r) => r.v > 0) : null
  const ranked = inRows ? [...inRows].sort((a, b) => b.v - a.v) : null
  const inTotal = inRows ? inRows.reduce((a, r) => a + r.v, 0) : null
  const inMax = ranked && ranked.length ? ranked[0].v : 1
  const inOrder = inRows ? [...inRows].sort((a, b) => a.name.localeCompare(b.name)) : null // stable DOM order; rows slide to their rank
  const people = (Y.data?.people ?? []).filter((p) => n(p.total) > 0)
  const daysSince = (d: unknown) => { const t = Date.parse(String(d ?? '')); return Number.isFinite(t) ? Math.max(0, Math.floor((Date.parse(today) - t) / 86_400_000)) : 0 }
  // one row per person (Tim, 29 Sep: "it's really just how much do we owe Masih"): Masih's consignor-key lines, his own
  // and Split pieces and anything bought from him are one line; a consignor who also sold us a piece is one line too.
  const isMasih = (p: Row | null, name: string) => !!(p && p.is_masih) || /\bmasih\b/i.test(name)
  const owedBy = new Map<string, { key: string; name: string; kind: string; v: number; days: number; to: string }>()
  const addOwed = (id: string, name: string, kind: string, v: number, days: number, to: string) => {
    const f = owedBy.get(id)
    if (f) { f.v += v; f.days = Math.max(f.days, days); if (f.kind !== kind && f.kind !== 'partner') f.kind = 'consignor' }
    else owedBy.set(id, { key: id, name, kind, v, days, to })
  }
  const masihPayee = people.find((p) => isMasih(p, String(p.name ?? '')))
  const masihTo = masihPayee ? `/payouts/${encodeURIComponent(String(masihPayee.payee))}` : '/payouts'
  if (Y.data) {
    for (const p of people) {
      const name = String(p.name ?? 'Consignor')
      if (isMasih(p, name)) addOwed('masih', 'Masih', 'partner', n(p.total), n(p.oldest_days), masihTo)
      else addOwed('n:' + name.trim().toLowerCase(), name, 'consignor', n(p.total), n(p.oldest_days), `/payouts/${encodeURIComponent(String(p.payee))}`)
    }
    for (const b of Y.data.bought) {
      const name = String(b.from_name ?? 'Seller')
      if (isMasih(null, name)) addOwed('masih', 'Masih', 'partner', n(b.cost), daysSince(b.date), masihTo)
      else addOwed('n:' + name.trim().toLowerCase(), name, 'seller', n(b.cost), daysSince(b.date), `/payouts/bought/${encodeURIComponent(String(b.intake_id))}`)
    }
  }
  const owed = Y.data ? [...owedBy.values()].filter((o) => o.v > 0).sort((a, b) => b.days - a.days || b.v - a.v) : null
  const owedTotal = owed ? owed.reduce((a, o) => a + o.v, 0) : null

  // 3. what needs attention: real problems only, red first. Older stock without a cost is not one (it is in the switchover).
  const items: Item[] = []
  if (Y.data) for (const p of people.filter((p) => n(p.oldest_days) > 30).sort((a, b) => n(b.oldest_days) - n(a.oldest_days)))
    items.push({ key: 'pay:' + String(p.payee), tone: 'red', text: `${p.name ?? 'A payee'} owed for ${n(p.oldest_days)} days`, go: `${usd(n(p.total))}. Record the payout.`, to: `/payouts/${encodeURIComponent(String(p.payee))}` })
  if (Hh.data && Hh.data.days_not_closed.length) {
    const d = Hh.data.days_not_closed
    items.push({ key: 'close', tone: 'red', text: `${d.length} ${d.length === 1 ? 'day' : 'days'} not closed`, go: d.slice(0, 3).map(dayDow).join(', ') + (d.length > 3 ? '…' : ''), to: '/daily' })
  }
  if (Hh.data) {
    const other = Hh.data.issues.filter((i) => i.severity === 'red' && !['day_not_closed', 'payout_overdue', 'line_no_cost', 'line_no_owner'].includes(String(i.kind))).length
    if (other) items.push({ key: 'health', tone: 'red', text: `${other} ${other === 1 ? 'problem' : 'problems'} in the data`, go: 'See what and how to fix', to: '/health' })
  }
  if (S && S.noOwnerCount) items.push({ key: 'owner', tone: 'amber', text: `${S.noOwnerCount} sold ${S.noOwnerCount === 1 ? 'piece has' : 'pieces have'} no owner`, go: `${usd(S.noOwner)} of sales. Sets what’s owed out.`, to: '/inventory/sold' })
  if (O.data) {
    const s = O.data.ties.shopify
    if (Math.abs(n(s.ledger) - n(s.mirror)) >= 1 || n(s.not_in_ledger) > 0 || n(s.differs) > 0)
      items.push({ key: 'shopify', tone: 'amber', text: n(s.differs) ? `${n(s.differs)} Shopify ${n(s.differs) === 1 ? 'order differs' : 'orders differ'} from the ledger` : 'Shopify orders and the ledger disagree', go: 'Open the tie-outs', to: '/month' })
  }
  items.sort((a, b) => (a.tone === b.tone ? 0 : a.tone === 'red' ? -1 : 1))
  const attnReady = !!(Y.data && Hh.data && O.data && L.data)
  const leaving = useLeaving(items, attnReady)

  // 4. the switchover: share of this month's sales tracked; pieces on the floor tracked (consigned, or with a cost)
  const floor = F.data ? { tracked: n(F.data.tiles.consignors) + n(F.data.tiles.real_cost) + n(F.data.tiles.typed), all: n(F.data.tiles.pieces) } : null

  const loading = <span className="rp-home__skel" />
  const err = L.error || O.error || Y.error || Hh.error
  const ticker = isCurrent && recent.length > 0 && (
    <div className="rp-live" title="This month's latest sales. Pauses while you look.">
      <span className="rp-live__dot"><i />Live</span>
      <div className="rp-live__marq"><div className="rp-live__run" style={{ animationDuration: `${Math.max(24, recent.length * 5)}s` }}>
        {[0, 1].map((copy) => recent.map(([k, s]) => (
          <span key={copy + k} className={seen.current && !seen.current.has(k) ? 'new' : undefined} aria-hidden={copy ? 'true' : undefined}><b>{s.no} {usd(s.v)}</b>{s.how}</span>
        )))}
      </div></div>
    </div>
  )
  return (
    <Screen title={monthName(month)} sub={isCurrent ? 'This month so far, up to today' : 'Closed month'}
      right={<div className="rp-home__right">{ticker}<MonthPicker month={month} onChange={setMonth} min={goLive.slice(0, 7)} /></div>}>
      {err && <div className="rp-banner" style={{ color: "#a4000f" }}>{err}</div>}
      <div className="rp-home">
        <section className="rp-home__card rp-home__hero rp-in" style={{ ['--d' as string]: '0ms' }}>
          <div className="rp-home__top">
            <div>
              <div className="rp-home__label">Net sales</div>
              <div className="rp-home__big"><Roll value={net} /></div>
              <div className={'rp-home__delta' + (change != null && change < 0 ? ' dn' : '')}>
                {count != null && <span className="rp-home__meta">{count} {count === 1 ? 'sale' : 'sales'}{avg != null ? ` · average ${usd(avg)}` : ''} · </span>}
                {change == null ? (L.data && P.data ? <span>no sales last month to compare</span> : ' ') : <>{change >= 0 ? '↑' : '↓'} {Math.abs(change).toFixed(1)}% <span>vs {monthName(prev).split(' ')[0]} at the same point</span></>}
              </div>
            </div>
            <div className="rp-home__side">
              <Tip body={S && <><h5>Starchive keeps</h5><Tr a="Net sales" b={usd(S.net)} />{S.toConsignors > 0 && <Tr a="To consignors" b={'−' + usd(S.toConsignors)} />}{S.toMasih > 0 && <Tr a="To Masih" b={'−' + usd(S.toMasih)} />}<Tr a="Keeps" b={usd(S.keep)} tot /><p>Complete on every sale with an owner, cost or not.</p></>}>
                <b><Roll value={S ? S.keep : null} /></b><span className="u">Starchive keeps</span>
              </Tip>
              <Tip body={S && <><h5>Profit, by where it comes from</h5><Tr a="Purchased, with cost" b={usd(S.buyProfit)} /><Tr a="Consigned (the fee)" b={usd(S.conProfit)} /><Tr a="Profit" b={usd(S.profit)} tot /><p>Purchased pieces (Shane&rsquo;s, Split, Masih&rsquo;s): price minus cost. Consigned: the fee. Before card fees and shipping.</p></>}>
                <b><Roll value={S ? S.profit : null} /></b><span className="u"><Swap text={complete ? 'profit' : 'profit, tracked pieces'} /></span>
              </Tip>
              <div><b><Roll value={margin} format={pct} flash={false} /></b><span><Swap text={complete ? 'margin' : 'margin, tracked pieces'} /></span></div>
            </div>
          </div>
          {S && S.net > 0 && (
            <div className="rp-split">
              <div className="rp-split__track">
                <Tip block body={<TrackedTip S={S} />}><i className="rp-split__t" style={{ width: `${share}%` }} /></Tip>
                <Tip block body={<UntrackedTip S={S} />}><i className="rp-split__u" style={{ width: `${100 - (share ?? 0)}%` }} /></Tip>
              </div>
              <div className="rp-split__keys">
                <Tip body={<TrackedTip S={S} />}><i className="k" /><b><Roll value={S.tracked} /></b><em>tracked, profit known</em></Tip>
                <Tip body={<UntrackedTip S={S} />}><i className="k u" /><b><Roll value={S.net - S.tracked} /></b><em>not tracked yet</em></Tip>
              </div>
            </div>
          )}
          {cur && last ? <MonthLine cur={cur} prev={last} curLabel={monthName(month).split(' ')[0]} prevLabel={monthName(prev).split(' ')[0]} days={Math.max(dim, daysIn(prev))} live={isCurrent} /> : <div className="rp-home__chart-skel" />}
        </section>

        <section className="rp-home__card rp-in" style={{ ['--d' as string]: '80ms' }}>
          <Head label="Money in" sub="received, by how it was paid" value={inTotal} />
          <div className="rp-home__rows rp-home__rows--rank" style={{ height: ranked ? Math.max(1, ranked.length) * 38 : undefined }}>
            {inOrder && ranked ? inOrder.map((r) => (
              <div key={r.name} className="rp-home__row" style={{ top: ranked.indexOf(r) * 38 }}><span>{r.name}</span><span className="rp-home__track"><i style={{ ['--w' as string]: `${(100 * r.v) / inMax}%` }} /></span><b><Roll value={r.v} /></b></div>
            )) : [0, 1, 2, 3].map((i) => <div key={i} className="rp-home__row" style={{ top: i * 38 }}>{loading}</div>)}
            {inRows && inRows.length === 0 && <div className="rp-home__none">Nothing received yet this month.</div>}
          </div>
        </section>

        <section className="rp-home__card rp-in" style={{ ['--d' as string]: '140ms' }}>
          <Head label="Owed out" sub={owed ? `${owed.length} ${owed.length === 1 ? 'person' : 'people'} · oldest first` : 'to Masih, consignors and sellers'} value={owedTotal} />
          <div className="rp-home__owed">
            {owed ? (owed.length ? <Revolve rowH={42} visible={5} every={OWED_EVERY} rows={owed.map((o) => ({ key: o.key, node: (
              <Link to={o.to} className="rp-home__o"><span>{o.name} <small>{o.kind}</small>{o.days > 30 && <em>{o.days} days</em>}</span><b><Roll value={o.v} flash={false} /></b></Link>
            ) }))} /> : <div className="rp-home__o rp-home__o--clear"><span>Everything owed is paid</span><b>✓</b></div>)
              : [0, 1, 2].map((i) => <div key={i} className="rp-home__o">{loading}</div>)}
          </div>
        </section>

        <section className="rp-home__card rp-in" style={{ ['--d' as string]: '200ms' }}>
          <div className="rp-home__head"><div><div className="rp-home__label">Needs attention</div><div className="rp-home__sub">{attnReady && items.length > 0 ? `${items.length} ${items.length === 1 ? 'thing' : 'things'} · most urgent first` : ' '}</div></div></div>
          <div className="rp-home__att">
            {leaving.map((it) => <div key={'gone-' + it.key} className="rp-home__it rp-home__it--fixed"><i /><span><span className="rp-home__text">✓ {it.text}</span><span className="rp-home__go">Done</span></span></div>)}
            {!attnReady ? [0, 1, 2].map((i) => <div key={i} className="rp-home__it">{loading}</div>)
              : items.length === 0 ? (leaving.length ? null : <div className="rp-home__clear">✓ Nothing needs attention.</div>)
              : <Revolve rowH={58} visible={3} every={ATT_EVERY} rows={items.map((it) => ({ key: it.key, node: (
                  <Link to={it.to} className={'rp-home__it rp-home__it--' + it.tone}><i /><span><span className="rp-home__text">{it.text}</span><span className="rp-home__go">{it.go}</span></span><span className="rp-home__ar">→</span></Link>
                ) }))} />}
          </div>
          <div className="rp-home__note">Older stock without a cost is not listed here. It&rsquo;s counted in the switchover.</div>
        </section>

        <section className="rp-home__card rp-in" style={{ ['--d' as string]: '260ms' }}>
          <div className="rp-home__label">Switchover to tracked costs</div>
          <div className="rp-sw">
            <Ring value={share} />
            <div className="rp-sw__text">
              <div className="rp-sw__pct"><Roll value={share} format={pct} /></div>
              <div className="rp-home__sub" style={{ marginTop: 0 }}>of this month&rsquo;s sales tracked</div>
              <div className="rp-sw__floor">
                <div><span>Pieces on the floor tracked</span><b>{floor ? `${floor.tracked} of ${floor.all}` : '…'}</b></div>
                <span className="rp-home__track"><i style={{ ['--w' as string]: floor && floor.all ? `${(100 * floor.tracked) / floor.all}%` : '0%' }} /></span>
                <small>consigned, or with a cost</small>
              </div>
            </div>
          </div>
          {complete ? <div className="rp-sw__done">✓ Profit is now complete for the month</div>
            : <div className="rp-home__note">At 95% (the mark at the top of the ring) profit drops the &ldquo;tracked pieces&rdquo; label and reads as the month&rsquo;s profit.</div>}
        </section>
      </div>
    </Screen>
  )
}

/** Items that were in Needs attention a moment ago and are gone now were fixed: they stay 1.7 s with a green tick, then leave. */
function useLeaving(items: Item[], ready: boolean) {
  const [leaving, setLeaving] = useState<Item[]>([])
  const prev = useRef<Item[] | null>(null)
  const sig = items.map((i) => i.key).join('|')
  useEffect(() => {
    if (!ready) return
    const before = prev.current; prev.current = items
    if (!before) return
    const now = new Set(items.map((i) => i.key))
    const gone = before.filter((b) => !now.has(b.key))
    if (!gone.length) return
    setLeaving((l) => [...l.filter((x) => !gone.some((g) => g.key === x.key)), ...gone])
    const t = setTimeout(() => setLeaving((l) => l.filter((x) => !gone.some((g) => g.key === x.key))), 1700)
    return () => clearTimeout(t)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, ready])
  return leaving
}

/** The switchover ring: fills around the slowly turning star; the mark at the top is 95%. */
function Ring({ value }: { value: number | null }) {
  const C = 2 * Math.PI * 54
  const [v, setV] = useState(0)
  useEffect(() => { if (value == null) return; const t = requestAnimationFrame(() => setV(value)); return () => cancelAnimationFrame(t) }, [value])
  return (
    <div className="rp-sw__ring" aria-hidden="true">
      <svg viewBox="0 0 132 132"><circle className="bg" cx="66" cy="66" r="54" /><line className="goal" x1="66" y1="4" x2="66" y2="18" /><circle className="fg" cx="66" cy="66" r="54" strokeDasharray={C} strokeDashoffset={C * (1 - Math.min(100, v) / 100)} /></svg>
      <i className="rp-sw__star" />
    </div>
  )
}

/** A label that fades to its new words. */
function Swap({ text }: { text: string }) {
  const [shown, setShown] = useState(text)
  const [out, setOut] = useState(false)
  useEffect(() => { if (text === shown) return; if (reduced()) { setShown(text); return } setOut(true); const t = setTimeout(() => { setShown(text); setOut(false) }, 220); return () => clearTimeout(t) }, [text, shown])
  return <span className={'rp-swap' + (out ? ' out' : '')}>{shown}</span>
}

/** Hover detail: how a figure is made. */
function Tip({ body, children, block }: { body: ReactNode; children: ReactNode; block?: boolean }) {
  return <div className={'rp-tipw' + (block ? ' rp-tipw--block' : '')} tabIndex={body ? 0 : undefined}>{children}{body && <div className="rp-tip" role="tooltip">{body}</div>}</div>
}
function Tr({ a, b, tot }: { a: string; b: string; tot?: boolean }) { return <div className={'rp-tip__r' + (tot ? ' tot' : '')}><span>{a}</span><span>{b}</span></div> }
function TrackedTip({ S }: { S: ReturnType<typeof split> }) {
  return <><h5>Tracked: profit known</h5><Tr a="Purchased, with cost" b={usd(S.buyNet)} /><Tr a="Consigned" b={usd(S.conNet)} /><Tr a="Sales" b={usd(S.tracked)} tot /></>
}
function UntrackedTip({ S }: { S: ReturnType<typeof split> }) {
  return <><h5>Not tracked yet</h5>{S.older > 0 && <Tr a="Older stock, no cost" b={usd(S.older)} />}{S.masihNoCost > 0 && <Tr a={"Masih\u2019s, no cost yet"} b={usd(S.masihNoCost)} />}{S.noOwner > 0 && <Tr a="No owner set yet" b={usd(S.noOwner)} />}<Tr a="Sales" b={usd(S.net - S.tracked)} tot /><p>Older stock shrinks as intake pieces sell. No-owner pieces are fixed from Needs attention.</p></>
}
function Head({ label, sub, value }: { label: string; sub: string; value: number | null }): ReactNode {
  return <div className="rp-home__head"><div><div className="rp-home__label">{label}</div><div className="rp-home__sub">{sub}</div></div><div className="rp-home__total"><Roll value={value} /></div></div>
}
